import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import {
  CASH_FLOW_TABS,
  cashFlowInitialValues,
} from 'components/organisms/forms/cash-flow/CashFlowForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useCustomParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetIncome,
  usePutIncome,
  usePutIncomeFile,
} from 'services/cash-flow/income';
import { ICashFlowForm } from 'services/cash-flow/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const EditIncome = () => {
  const labelId = useCustomParamId('id', '/label');
  const incomeId = useCustomParamId('cashFlowId', `/label/${labelId}/income`);

  const { pathname } = useLocation();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const mutateOptions = useMutateOptions<ICashFlowForm>();

  const income = useGetIncome(incomeId);

  const updateIncome = usePutIncome();

  const addFile = usePutIncomeFile();

  const onGoToDetail = () =>
    navigate(
      `/label/${labelId}/income/${incomeId}/${getLastPathname(pathname)}`
    );

  const addFileToIncome = async (file: File) => {
    await addFile.mutateAsync(
      { file, id: incomeId },
      {
        onSuccess: () => onGoToDetail(),
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const onUpdateIncome = async (
    values: ICashFlowForm,
    formikHelpers: FormikHelpers<ICashFlowForm>
  ) => {
    await updateIncome.mutateAsync(
      {
        ...middleware.income.formToQuery(values),
        settlementIncomeId: incomeId,
      },
      {
        ...mutateOptions(formikHelpers),
        onSuccess: () =>
          values.file ? addFileToIncome(values.file) : onGoToDetail(),
      }
    );
  };

  return (
    <Form.CashFlow
      initialValues={
        income.data
          ? middleware.income.queryToForm(income.data)
          : cashFlowInitialValues
      }
      onSubmit={onUpdateIncome}
      disableTabs={[CASH_FLOW_TABS.repayments]}
      status={{ isLoading: income.isLoading }}
      customBackItem={
        <Button onlyIcon onClick={onGoToDetail}>
          <Button.Description description={t('button.cancel')} />

          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <Button type='submit' variant={BUTTON_VARIANT.submit}>
          <Button.Text text={t('button.edit')} />
        </Button>
      }
    />
  );
};

export default EditIncome;
